/**
 * The in-app notification feed (the header bell).
 *
 * Dual-mode per docs/patterns/00-lovable-preview-mode.md: preview serves
 * `dummy-notifications.ts` and mutations are no-op-but-successful; production talks to
 * the backend. Both branches return the identical shape, and the preview branch sits
 * after every hook call — an early return before them is COMMON_BUGS.md's React Error
 * 310.
 *
 * `NotificationBell` lives inside the always-mounted `AppLayout`, which makes this hook
 * a textbook case of COMMON_BUGS.md:1248 (hooks auto-fetching in always-mounted
 * components). Hence the gating in §queries below: the badge count polls cheaply and
 * only while the tab is visible; the list does not run at all until the popover opens.
 *
 * See docs/patterns/62-notification-feed.md.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApolloError } from "@apollo/client";

import { dummyNotifications } from "@/data/dummy-notifications";
import {
  GetNotificationNewCountDocument,
  type GetNotificationNewCountQuery,
  GetNotificationUnreadCountDocument,
  type GetNotificationUnreadCountQuery,
  useGetNotificationNewCountQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationsSeenMutation,
  useGetNotificationsLazyQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
} from "@/lib/graphql/types-and-hooks";
import { useDocumentVisibility } from "@/lib/hooks/use-document-visibility";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { parseNotificationTarget } from "@/lib/notifications/target";
import { resolveNotificationType, type FeedNotification } from "@/lib/notifications/types";
import { isLovablePreview } from "@/lib/utils/environment";

/** Fills the popover; the backend caps requests well above this. */
export const DEFAULT_PAGE_SIZE = 20;
/** Badge refresh cadence. There are no GraphQL subscriptions, so the client polls. */
export const UNREAD_POLL_MS = 60_000;

export interface UseNotificationFeedOptions {
  /** Feature-flag gate. When false the hook is inert in both modes. */
  enabled: boolean;
  /** Popover open state. Gates the LIST query only — the badge keeps polling. */
  open: boolean;
  pageSize?: number;
  pollIntervalMs?: number;
}

export interface UseNotificationFeedResult {
  notifications: FeedNotification[];
  unreadCount: number;
  /**
   * Badge count: arrived since the bell was last opened. A different question from
   * `unreadCount`, which drives the Mark-all-read control — that is what lets opening the
   * bell clear the badge while every row stays bold until the user acts on it.
   */
  newCount: number;
  /** Clears the badge without marking anything read. Call when the popover opens. */
  markSeen: () => Promise<{ success: boolean; error?: unknown }>;
  loading: boolean;
  error?: ApolloError;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<{ success: boolean; error?: unknown }>;
  markAllRead: () => Promise<{ success: boolean; error?: unknown }>;
}

/** Wire row -> domain row. Exported for its own test; no React, no Apollo. */
export function toFeedNotification(node: {
  id: string;
  type?: string | null;
  params?: unknown;
  target?: unknown;
  fallbackTitle?: string | null;
  fallbackBody?: string | null;
  readTs?: unknown;
  createTs?: unknown;
}): FeedNotification {
  const asIso = (v: unknown): string | null => {
    if (!v) return null;
    if (typeof v === "string") return v;
    if (v instanceof Date) return v.toISOString();
    return null;
  };

  /**
   * `params` and `target` are graphene JSONString scalars, which cross the wire as
   * JSON *strings* rather than objects — see use-agents.ts ("JSON.stringify out,
   * JSON.parse in"). Treating a string as an object silently yields an empty object,
   * which costs every row its CTA and every pluralised body its `count`, so the copy
   * falls back to the raw i18n key.
   *
   * Tolerates an already-parsed object too: Apollo's cache and the preview fixtures
   * both hand these over as objects, and a mapper that only worked for one of them
   * would break the other.
   */
  const asJson = (v: unknown): Record<string, unknown> => {
    if (!v) return {};
    if (typeof v === "object") return v as Record<string, unknown>;
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  };

  return {
    id: String(node.id),
    // Null for a type this build does not know. The backend deploys independently, so
    // this is expected, not an error — the row renders from fallbackTitle instead.
    type: resolveNotificationType(node.type),
    createdAt: asIso(node.createTs) ?? new Date().toISOString(),
    readAt: asIso(node.readTs),
    params: asJson(node.params) as Record<string, string | number>,
    // Total by construction: anything malformed degrades to { kind: "none" }.
    target: parseNotificationTarget(asJson(node.target)),
    fallbackTitle: node.fallbackTitle ?? "",
    fallbackBody: node.fallbackBody ?? null,
  };
}

export function useNotificationFeed(
  opts: UseNotificationFeedOptions,
): UseNotificationFeedResult {
  const { enabled, open, pageSize = DEFAULT_PAGE_SIZE, pollIntervalMs = UNREAD_POLL_MS } = opts;

  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const isVisible = useDocumentVisibility();

  // --- Preview state. Held unconditionally so hook order never changes with mode.
  const [previewRows, setPreviewRows] = useState<FeedNotification[]>(dummyNotifications);

  // --- Production: the badge. "Arrived since I last opened the bell", which is why opening
  // can clear it without touching read state. Polls; not derived from the list.
  const newCountQuery = useGetNotificationNewCountQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: preview || !enabled || !workspaceId,
    pollInterval: isVisible && enabled ? pollIntervalMs : 0,
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "cache-and-network",
  });

  // --- Production: unread count. Drives the Mark-all-read control, not the badge. Fetched
  // only while the popover is open, since that is the only place it is shown.
  const countQuery = useGetNotificationUnreadCountQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: preview || !enabled || !workspaceId || !open,
    // Polling stops when the tab is hidden. Precedent: use-crm-sync-status.ts.
    pollInterval: isVisible && enabled && open ? pollIntervalMs : 0,
    // COMMON_BUGS.md:418 — leaving this on re-renders on every network status change and
    // is half of the network-only + normalized-cache loop.
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "cache-and-network",
  });

  // --- Production: the list. Lazy, so nothing fetches until the popover opens.
  const [fetchFeed, feedQuery] = useGetNotificationsLazyQuery({
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "cache-and-network",
  });

  // Guards the "returned fewer than asked for => end of data" safeguard so it only
  // applies to a real page fetch. COMMON_BUGS.md:574: markRead's cache.modify re-emits
  // the list with an unchanged node count, which without this reads as "no more pages"
  // and permanently kills Load More after the first row is read.
  const paginatingRef = useRef(false);
  // Ensures the first open fetches exactly once rather than on every render.
  const openedOnceRef = useRef(false);

  useEffect(() => {
    if (preview || !enabled || !workspaceId) return;
    if (!open) {
      openedOnceRef.current = false;
      return;
    }
    if (openedOnceRef.current) return;
    openedOnceRef.current = true;
    // Deliberately not in the dep array: including the lazy-query function is the other
    // half of the COMMON_BUGS.md:418 loop.
    void fetchFeed({ variables: { workspaceId, limit: pageSize } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, enabled, workspaceId, open, pageSize]);

  const [markReadMutation] = useMarkNotificationsReadMutation();
  const [markAllReadMutation] = useMarkAllNotificationsReadMutation();
  const [markSeenMutation] = useMarkNotificationsSeenMutation();

  const productionRows = useMemo(
    () => (feedQuery.data?.notifications?.nodes ?? []).filter(Boolean).map(n => toFeedNotification(n as never)),
    [feedQuery.data],
  );

  const productionHasMore = feedQuery.data?.notifications?.hasMore ?? false;

  const loadMore = useCallback(async () => {
    if (preview) return;
    if (!workspaceId || !feedQuery.data?.notifications?.nodes?.length) return;
    if (!productionHasMore || paginatingRef.current) return;

    const nodes = feedQuery.data.notifications.nodes;
    const oldest = nodes[nodes.length - 1];
    if (!oldest?.id) return;

    paginatingRef.current = true;
    try {
      await feedQuery.fetchMore?.({
        variables: { workspaceId, limit: pageSize, beforeId: oldest.id },
        // COMMON_BUGS.md:520 — keyset pagination has no Relay cursor for Apollo to merge,
        // so without this the new page replaces the old one instead of appending.
        // Do NOT also add a typePolicies merge for NotificationConnection: a field policy
        // plus updateQuery double-merges.
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.notifications) return prev;
          const seen = new Set((prev.notifications?.nodes ?? []).map(n => n?.id));
          const incoming = (fetchMoreResult.notifications.nodes ?? []).filter(
            n => n && !seen.has(n.id),
          );
          return {
            ...prev,
            notifications: {
              ...fetchMoreResult.notifications,
              nodes: [...(prev.notifications?.nodes ?? []), ...incoming],
            },
          };
        },
      });
    } finally {
      paginatingRef.current = false;
    }
  }, [preview, workspaceId, feedQuery, pageSize, productionHasMore]);

  const markRead = useCallback(
    async (id: string) => {
      if (preview) {
        setPreviewRows(rows =>
          rows.map(r => (r.id === id && !r.readAt ? { ...r, readAt: new Date().toISOString() } : r)),
        );
        return { success: true };
      }
      if (!workspaceId) return { success: false };

      try {
        await markReadMutation({
          variables: { workspaceId, ids: [id] },
          // House rule (CLAUDE.md): no refetchQueries on update mutations — it would
          // reset pagination. Template: use-record-grants.ts.
          update(cache, { data }) {
            const payload = data?.markNotificationsRead;
            const readTs = payload?.notifications?.[0]?.readTs ?? new Date().toISOString();
            cache.modify({
              id: cache.identify({ __typename: "NotificationType", id }),
              fields: { readTs: () => readTs },
            });
            // notificationUnreadCount is a root field WITH arguments, so cache.modify on
            // it would clobber every cached workspace's count. Write this workspace's.
            cache.writeQuery<GetNotificationUnreadCountQuery>({
              query: GetNotificationUnreadCountDocument,
              variables: { workspaceId },
              data: { notificationUnreadCount: payload?.unreadCount ?? 0 },
            });
          },
        });
        return { success: true };
      } catch (error) {
        // Swallowed: a failed mark-read must not unmount the popover with an unhandled
        // rejection. Same contract as use-record-grants.ts.
        return { success: false, error };
      }
    },
    [preview, workspaceId, markReadMutation],
  );

  const markSeen = useCallback(async () => {
    // Preview has no server marker; the badge is derived from dummy rows, so there is
    // nothing to clear and claiming success is honest enough for a UI-only mode.
    if (preview || !workspaceId) return { success: true };

    try {
      await markSeenMutation({
        variables: { workspaceId },
        update(cache, { data }) {
          // Root field WITH arguments, so cache.modify would clobber every cached
          // workspace's count. writeQuery keyed on this workspace, from the mutation's
          // authoritative return value rather than an assumed 0.
          cache.writeQuery<GetNotificationNewCountQuery>({
            query: GetNotificationNewCountDocument,
            variables: { workspaceId },
            data: { notificationNewCount: data?.markNotificationsSeen?.newCount ?? 0 },
          });
        },
      });
      return { success: true };
    } catch (error) {
      // Swallowed: a failed mark-seen leaves the badge up, which is a cosmetic annoyance
      // and self-corrects on the next open. Blocking the popover would be worse.
      return { success: false, error };
    }
  }, [preview, workspaceId, markSeenMutation]);

  const markAllRead = useCallback(async () => {
    if (preview) {
      const now = new Date().toISOString();
      setPreviewRows(rows => rows.map(r => (r.readAt ? r : { ...r, readAt: now })));
      return { success: true };
    }
    if (!workspaceId) return { success: false };

    try {
      await markAllReadMutation({
        variables: { workspaceId },
        update(cache, { data }) {
          const now = new Date().toISOString();
          // Only the rows this client happens to hold; the server marked everything, and
          // rows never loaded simply arrive read.
          for (const row of feedQuery.data?.notifications?.nodes ?? []) {
            if (!row?.id) continue;
            cache.modify({
              id: cache.identify({ __typename: "NotificationType", id: row.id }),
              fields: { readTs: (existing: unknown) => existing ?? now },
            });
          }
          cache.writeQuery<GetNotificationUnreadCountQuery>({
            query: GetNotificationUnreadCountDocument,
            variables: { workspaceId },
            data: { notificationUnreadCount: data?.markAllNotificationsRead?.unreadCount ?? 0 },
          });
        },
      });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [preview, workspaceId, markAllReadMutation, feedQuery.data]);

  // --- Every hook above runs in both modes. Only now does the branch happen, and both
  // arms return the same shape (the use-tasks.tsx convention).
  if (preview) {
    return {
      notifications: previewRows,
      unreadCount: previewRows.filter(r => !r.readAt).length,
      // No server "seen" marker in preview, so the badge mirrors unread there.
      newCount: previewRows.filter(r => !r.readAt).length,
      markSeen,
      loading: false,
      error: undefined,
      hasMore: false,
      loadMore: async () => {},
      markRead,
      markAllRead,
    };
  }

  return {
    notifications: productionRows,
    unreadCount: countQuery.data?.notificationUnreadCount ?? 0,
    newCount: newCountQuery.data?.notificationNewCount ?? 0,
    markSeen,
    loading: feedQuery.loading,
    error: feedQuery.error ?? newCountQuery.error ?? countQuery.error,
    hasMore: productionHasMore,
    loadMore,
    markRead,
    markAllRead,
  };
}
